import React, { useMemo, useState } from 'react'
import { Box, Card, CardContent, CardHeader, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip, Tooltip, Autocomplete } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TodayIcon from '@mui/icons-material/Today'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { timesheetsAPI, employeesAPI, projectsAPI } from '../services/api'
import { ROLES, REQUEST_TYPE_LABELS, TYPE_COLORS } from '../constants'
import { Timesheet, TimesheetFormData, Project, Employee, TimeOffRequest } from '../types'
import { useAuth } from '../contexts/AuthContext'
import { useHolidays } from '../hooks/useHolidays'
import { useTimeOffCalendar } from '../hooks/useTimeOff'

const isCurrentlyAssigned = (assignment: { startDate?: string; endDate?: string | null }, asOf = new Date()) => {
  const startDate = assignment.startDate ? new Date(assignment.startDate) : null
  const endDate = assignment.endDate ? new Date(assignment.endDate) : null
  if (startDate && startDate > asOf) return false
  if (endDate && endDate < asOf) return false
  return true
}

function startOfMonth(date: Date) { const d = new Date(date); d.setDate(1); d.setHours(0,0,0,0); return d; }
function endOfMonth(date: Date) { const d = new Date(date); d.setMonth(d.getMonth()+1); d.setDate(0); d.setHours(23,59,59,999); return d; }
function getMonthMatrix(current: Date) {
  const start = startOfMonth(current);
  const end = endOfMonth(current);
  const startDay = new Date(start); startDay.setDate(start.getDate() - ((start.getDay()+6)%7)); // Monday as first day
  const weeks: Date[][] = [];
  let cursor = new Date(startDay);
  while (cursor <= end || weeks.length < 6) {
    const week: Date[] = [];
    for (let i=0;i<7;i++) { week.push(new Date(cursor)); cursor.setDate(cursor.getDate()+1); }
    weeks.push(week);
  }
  return weeks;
}

const Timesheets: React.FC = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [editingEntry, setEditingEntry] = useState<Timesheet | null>(null)
  const [form, setForm] = useState<TimesheetFormData>({ projectId: '', date: '', hours: 8, description: '' })
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | 'ALL'>('ALL')
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [errorText, setErrorText] = useState<string | null>(null)

  const monthRange = useMemo(() => ({
    start: startOfMonth(currentMonth).toISOString(),
    end: endOfMonth(currentMonth).toISOString()
  }), [currentMonth])

  // Admin: employees list for filtering and assignment
  const { data: employees = [] } = useQuery<Employee[]>({
    queryKey: ['employees'],
    queryFn: () => employeesAPI.getActive(),
    enabled: user?.role === ROLES.ADMIN
  })

  // Fetch projects - employees get only their assigned projects; admins get all
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects', user?.role],
    queryFn: async () => {
      if (user?.role === ROLES.ADMIN) {
        return await projectsAPI.getAll()
      } else {
        return await projectsAPI.getMyProjects()
      }
    }
  })

  // Default selected project and guard invalid employee selection for this project
  React.useEffect(() => {
    if (!projects || projects.length === 0) return
    const firstActive = projects.find((p: any) => p.isActive)
    if (firstActive && !selectedProjectId) setSelectedProjectId(firstActive.id)
    // If currently selected employee is not on the selected project, reset to ALL
    const currentProject = projects.find((p: any) => p.id === (selectedProjectId || firstActive?.id))
    const allowedIds = new Set<string>(
      (currentProject?.employees || [])
        .filter((e: any) => isCurrentlyAssigned(e))
        .map((e: any) => e.id)
    )
    if (user?.role === ROLES.ADMIN && selectedEmployeeId !== 'ALL' && !allowedIds.has(selectedEmployeeId as string)) {
      setSelectedEmployeeId('ALL')
    }
  }, [projects, selectedProjectId])

  // Visible employees for admin: currently assigned to the selected project and still active
  const visibleEmployees: Employee[] = useMemo(() => {
    if (user?.role !== ROLES.ADMIN) return []
    const project = projects.find((p: any) => p.id === selectedProjectId)
    const ids = new Set<string>(
      (project?.employees || [])
        .filter((e: any) => isCurrentlyAssigned(e))
        .map((e: any) => e.id)
    )
    return employees
      .filter((e: Employee) => ids.has(e.id) && e.isActive)
      .sort((a: Employee, b: Employee) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
  }, [employees, projects, selectedProjectId, user?.role])

  // Fetch timesheets for month - backend filters by role; for admin + employee filter, pass employeeId
  const { data: timesheets = [] } = useQuery<Timesheet[]>({
    queryKey: ['timesheets', monthRange, user?.role, selectedEmployeeId, selectedProjectId],
    queryFn: async () => {
      const params: any = { startDate: monthRange.start, endDate: monthRange.end }
      if (user?.role === ROLES.ADMIN && selectedEmployeeId !== 'ALL') params.employeeId = selectedEmployeeId
      if (selectedProjectId) params.projectId = selectedProjectId
      return await timesheetsAPI.getAll(params)
    }
  })

  const createMutation = useMutation({
    mutationFn: async (payload: TimesheetFormData) => {
      return await timesheetsAPI.create(payload)
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['timesheets'] }); setErrorText(null); },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Failed to save. Please check inputs.'
      setErrorText(msg)
      if (msg.toLowerCase().includes('already exists') && selectedDate) {
        // Try to find the existing entry in current cache and open it for editing
        const existing = timesheets.find(t => {
          const sameDay = new Date(t.date).toDateString() === selectedDate.toDateString()
          const sameProject = t.projectId === form.projectId
          const sameEmployee = user?.role === ROLES.ADMIN ? (selectedEmployeeId==='ALL' || t.employeeId === selectedEmployeeId) : true
          return sameDay && sameProject && sameEmployee
        })
        if (existing) {
          openEdit(existing)
        }
      }
    }
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & TimesheetFormData) => {
      return await timesheetsAPI.update(id, payload)
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['timesheets'] }); setErrorText(null); },
    onError: (err: any) => setErrorText(err?.response?.data?.error || 'Failed to save. Please check inputs.')
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await timesheetsAPI.delete(id)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timesheets'] })
  })

  const { holidays = [] } = useHolidays()

  // Fetch approved time off requests for calendar view
  const { calendarData: timeOffCalendar = [] } = useTimeOffCalendar()

  // Time off entries visible in this view (current user or selected employee for admins)
  const visibleTimeOffRequests: TimeOffRequest[] = useMemo(() => {
    if (!timeOffCalendar || timeOffCalendar.length === 0) return []

    if (user?.role === ROLES.EMPLOYEE) {
      const employeeId = user.employee?.id
      if (!employeeId) return []
      return (timeOffCalendar as TimeOffRequest[]).filter(req => req.employee?.id === employeeId)
    }

    if (user?.role === ROLES.ADMIN && selectedEmployeeId !== 'ALL') {
      return (timeOffCalendar as TimeOffRequest[]).filter(req => req.employee?.id === selectedEmployeeId)
    }

    return []
  }, [timeOffCalendar, selectedEmployeeId, user?.role, user?.employee?.id])

  const weeks = useMemo(() => getMonthMatrix(currentMonth), [currentMonth])
  const isSameDay = (a: Date, bISO: string) => {
    const b = new Date(bISO); return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate()
  }

  // Check if a day is a holiday
  const getHolidayForDay = (day: Date) => {
    const dayDate = new Date(day)
    dayDate.setHours(0, 0, 0, 0)
    
    return holidays.find(holiday => {
      if (!holiday.isActive) return false
      
      const holidayStart = new Date(holiday.startDate)
      const holidayEnd = new Date(holiday.endDate)
      holidayStart.setHours(0, 0, 0, 0)
      holidayEnd.setHours(23, 59, 59, 999)
      
      return dayDate >= holidayStart && dayDate <= holidayEnd
    })
  }

  // Check if a day is a day off for the visible employee
  const getDayOffForDay = (day: Date) => {
    if (!visibleTimeOffRequests.length) return null

    const dayStart = new Date(day)
    dayStart.setHours(0, 0, 0, 0)
    const dayEnd = new Date(day)
    dayEnd.setHours(23, 59, 59, 999)

    return visibleTimeOffRequests.find(request => {
      const start = new Date(request.startDate)
      const end = new Date(request.endDate)
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)
      return start <= dayEnd && end >= dayStart
    }) || null
  }

  // Calculate total hours for the month when an employee is selected
  const totalHoursForMonth = useMemo(() => {
    if (user?.role !== ROLES.ADMIN || selectedEmployeeId === 'ALL') return null
    return timesheets.reduce((sum, entry) => {
      const entryDate = new Date(entry.date)
      if (entryDate.getMonth() === currentMonth.getMonth() && 
          entryDate.getFullYear() === currentMonth.getFullYear() &&
          entry.employeeId === selectedEmployeeId) {
        return sum + entry.hours
      }
      return sum
    }, 0)
  }, [timesheets, selectedEmployeeId, currentMonth, user?.role])

  const openForDate = (day: Date) => {
    setSelectedDate(day)
    setEditingEntry(null)
    const y = day.getFullYear();
    const m = String(day.getMonth() + 1).padStart(2, '0');
    const d = String(day.getDate()).padStart(2, '0');
    const localDate = `${y}-${m}-${d}`; // keep local calendar date
    setForm({ projectId: selectedProjectId || '', date: localDate, hours: 8, description: '', ...(user?.role===ROLES.ADMIN && selectedEmployeeId!=='ALL' ? { employeeId: selectedEmployeeId as string } : {}) })
    setDialogOpen(true)
  }

  const openEdit = (entry: Timesheet) => {
    setSelectedDate(new Date(entry.date))
    setEditingEntry(entry)
    setForm({ projectId: entry.projectId, date: entry.date.split('T')[0], hours: entry.hours, description: entry.description })
    setDialogOpen(true)
  }

  const submit = async () => {
    if (!form.projectId || !form.date || !form.hours) { setErrorText('Project, date and hours are required.'); return }
    // For admin, ensure target employee is selected or provided
    let effectiveEmployeeId: string | undefined = form.employeeId
    if (user?.role === ROLES.ADMIN) {
      if (selectedEmployeeId === 'ALL' && !form.employeeId) {
        setErrorText('Please select an employee.');
        return
      }
      effectiveEmployeeId = (selectedEmployeeId !== 'ALL') ? (selectedEmployeeId as string) : form.employeeId
    }

    // Backend expects z.string().datetime() with an offset; normalize to ISO string with Z
    const normalizedDate = form.date.includes('T')
      ? new Date(form.date).toISOString()
      : new Date(`${form.date}T00:00`).toISOString();

    const payload: TimesheetFormData = { ...form, date: normalizedDate, ...(effectiveEmployeeId ? { employeeId: effectiveEmployeeId } : {}) }

    try {
      if (editingEntry) await updateMutation.mutateAsync({ id: editingEntry.id, ...payload })
      else await createMutation.mutateAsync(payload)
      setDialogOpen(false)
    } catch (e) {
      // errorText set in onError
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, boxShadow: 6 }}>
        <CardHeader
          title={<Typography variant="h5" fontWeight={700}>Timesheets</Typography>}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 240 }}
                options={[...projects]
                  .filter((p: Project) => p.isActive)
                  .sort((a, b) => a.name.localeCompare(b.name))}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={projects.find((p) => p.id === selectedProjectId && p.isActive) || null}
                onChange={(_, newValue) => setSelectedProjectId(newValue?.id || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Project" />
                )}
              />
              {user?.role === ROLES.ADMIN && (
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220 }}
                  options={visibleEmployees}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={visibleEmployees.find((e) => e.id === selectedEmployeeId) || null}
                  onChange={(_, newValue) => setSelectedEmployeeId(newValue ? newValue.id : 'ALL')}
                  renderInput={(params) => (
                    <TextField {...params} label="Employee" placeholder="All Employees" />
                  )}
                />
              )}
              <IconButton onClick={() => setCurrentMonth(new Date(new Date(currentMonth).setMonth(currentMonth.getMonth()-1)))}><ChevronLeftIcon /></IconButton>
              <Button startIcon={<TodayIcon />} onClick={() => setCurrentMonth(new Date())}>Today</Button>
              <IconButton onClick={() => setCurrentMonth(new Date(new Date(currentMonth).setMonth(currentMonth.getMonth()+1)))}><ChevronRightIcon /></IconButton>
            </Box>
          }
          subheader={
            (() => {
              const monthText = new Date(currentMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              if (totalHoursForMonth !== null) {
                return (
                  <Box component="span">
                    {monthText} - {<Typography component="span" sx={{ color: 'success.main' }}>{totalHoursForMonth}h</Typography>}
                  </Box>
                )
              }
              return monthText
            })()
          }
        />
        <CardContent>
          {/* Calendar grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
              <Box key={d} sx={{ textAlign: 'center', py: 1, color: 'text.secondary', fontWeight: 600 }}>{d}</Box>
            ))}
            {weeks.flat().map((day, idx) => {
              const inMonth = day.getMonth() === currentMonth.getMonth()
              const isToday = day.toDateString() === new Date().toDateString()
              const entries = timesheets.filter(t => isSameDay(day, t.date))
              const total = entries.reduce((s, e) => s + e.hours, 0)
              const isAdmin = user?.role === ROLES.ADMIN
              const dayHoliday = getHolidayForDay(day)
              const isHoliday = !!dayHoliday
              const dayOff = getDayOffForDay(day)
              const isDayOff = !!dayOff
              return (
                <Box key={idx} sx={{
                  minHeight: 120,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: inMonth ? 'background.default' : 'action.hover',
                  position: 'relative'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography 
                      variant="caption" 
                      sx={{
                        fontWeight: 400,
                        fontSize: '0.75rem',
                        bgcolor: isToday ? 'primary.main' : 'transparent',
                        color: isToday ? 'white' : 'text.secondary',
                        borderRadius: isToday ? '50%' : 'none',
                        width: isToday ? 30 : 'auto',
                        height: isToday ? 20 : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: isToday ? 30 : 'auto'
                      }}
                    >
                      {day.getDate()}
                    </Typography>
                    <Chip size="small" label={`${total}h`} color={total >= 8 ? 'success' : 'default'} />
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 120, overflowY: 'auto' }}>
                    {isHoliday && (
                      <Tooltip title={dayHoliday.description || dayHoliday.name} arrow>
                        <Chip
                          label={dayHoliday.name}
                          size="small"
                          sx={{
                            bgcolor: 'warning.main',
                            color: 'black',
                            fontSize: '0.7rem',
                            height: 20,
                            width: '100%',
                            cursor: 'default',
                            mb: 0.5,
                          }}
                        />
                      </Tooltip>
                    )}
                    {isDayOff && (
                      <Chip
                        label={REQUEST_TYPE_LABELS[dayOff.type]}
                        size="small"
                        color={TYPE_COLORS[dayOff.type] as any}
                        sx={{
                          fontSize: '0.7rem',
                          height: 20,
                          width: '100%',
                          cursor: 'default',
                          mb: 0.5,
                        }}
                      />
                    )}
                    {entries.map(e => {
                      // For admins: show employee names, for employees: show descriptions
                      const displayText = isAdmin 
                        ? (e.employee?.firstName && e.employee?.lastName 
                           ? `${e.employee.firstName} ${e.employee.lastName.substring(0, 1)}.`
                           : 'Unknown Employee')
                        : (e.description || e.project?.name || 'Project');
                      
                      return (
                        <Button key={e.id} variant="outlined" size="small" sx={{ justifyContent: 'space-between' }} onClick={() => openEdit(e)}>
                          <Typography variant="caption" sx={{ mr: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {displayText}
                          </Typography>
                          <Typography variant="caption">{e.hours}h</Typography>
                        </Button>
                      );
                    })}
                    {inMonth && (
                      <Button 
                        variant="contained" 
                        color="inherit" 
                        size="small" 
                        startIcon={<AddIcon />} 
                        onClick={() => openForDate(day)}
                        disabled={isHoliday || (user?.role === ROLES.ADMIN && (selectedProjectId === '' || selectedEmployeeId === 'ALL'))}
                      >
                        <Typography sx={{ fontSize: '12px' }}>Log hours</Typography>
                      </Button>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEntry ? 'Edit Timesheet' : 'Log Hours'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} InputLabelProps={{ shrink: true }} disabled />
            <TextField label="Hours" type="number" inputProps={{ step: 0.5, min: 0, max: 24 }} value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} />
            <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Box>
          {(errorText) && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorText}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          {editingEntry && (
            <IconButton color="error" onClick={async () => { await deleteMutation.mutateAsync(editingEntry.id); setDialogOpen(false); }}>
              <DeleteIcon />
            </IconButton>
          )}
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={createMutation.isPending || updateMutation.isPending}>
            {editingEntry ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Timesheets 